import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { StripeProvider } from './StripeProvider';
import { SubscriptionStatus } from './SubscriptionStatus';
import { PricingPlans } from './PricingPlans';
import { PaymentMethodForm } from './PaymentMethodForm';
import { PaymentMethodList } from './PaymentMethodList';
import { InvoiceHistory } from './InvoiceHistory';
import { UsageDisplay } from './UsageDisplay';
import { billingAPI, SubscriptionPlan } from '../../services/api';

type Tab = 'overview' | 'plans' | 'payment' | 'invoices';

export const BillingDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState<number | undefined>();
  const [subscriptionId, setSubscriptionId] = useState<number | undefined>();

  React.useEffect(() => {
    loadCurrentPlan();
  }, []);

  const loadCurrentPlan = async () => {
    try {
      const response = await billingAPI.getCurrentSubscription();
      if (response.data.subscription) {
        setCurrentPlanId(response.data.subscription.plan?.id);
        setSubscriptionId(response.data.subscription.id);
      }
    } catch (err) {
      console.error('Failed to load current plan:', err);
    }
  };

  const handleSelectPlan = async (plan: SubscriptionPlan) => {
    if (subscriptionId && currentPlanId !== plan.id) {
      // Changing plan
      try {
        await billingAPI.updateSubscription(subscriptionId, {
          plan_id: plan.id,
        });
        loadCurrentPlan();
        setActiveTab('overview');
      } catch (err) {
        console.error('Failed to change plan:', err);
      }
    } else if (!subscriptionId) {
      // New subscription - redirect to checkout
      const successUrl = `${window.location.origin}/billing?success=true`;
      const cancelUrl = `${window.location.origin}/billing?canceled=true`;

      try {
        const response = await billingAPI.createCheckoutSession(
          plan.id,
          1,
          successUrl,
          cancelUrl
        );
        window.location.href = response.data.url;
      } catch (err) {
        console.error('Failed to create checkout session:', err);
      }
    }
  };

  const handlePaymentMethodAdded = () => {
    setShowAddPayment(false);
    // Refresh the page or update state as needed
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: t('billing.tabs.overview') },
    { key: 'plans', label: t('billing.tabs.plans') },
    { key: 'payment', label: t('billing.tabs.payment') },
    { key: 'invoices', label: t('billing.tabs.invoices') },
  ];

  return (
    <StripeProvider>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('billing.title')}
        </h1>

        {/* Tabs */}
        <div className={`relative border-b mb-8 -mx-4 sm:mx-0 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          {/* Scroll fade indicator */}
          <div className={`absolute right-0 top-0 bottom-0 w-8 pointer-events-none sm:hidden ${isDark ? 'bg-gradient-to-l from-slate-800 to-transparent' : 'bg-gradient-to-l from-white to-transparent'}`} />
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto hide-scrollbar px-4 sm:px-0">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-cyan-500 text-cyan-500'
                    : isDark
                      ? 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('billing.subscription')}
              </h2>
              <SubscriptionStatus
                onManageClick={() => setActiveTab('plans')}
                onUpgradeClick={() => setActiveTab('plans')}
              />
            </div>
            <div>
              <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('billing.currentUsage')}
              </h2>
              <div className={`rounded-xl shadow-lg p-6 ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white'}`}>
                <UsageDisplay />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'plans' && (
          <div>
            <h2 className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('billing.choosePlan')}
            </h2>
            <PricingPlans
              currentPlanId={currentPlanId}
              onSelectPlan={handleSelectPlan}
            />
          </div>
        )}

        {activeTab === 'payment' && (
          <div>
            <h2 className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('billing.paymentMethods')}
            </h2>

            {showAddPayment ? (
              <div className={`rounded-xl shadow-lg p-6 max-w-md ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-md font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {t('billing.addPaymentMethod')}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowAddPayment(false)}
                    className={`p-1 rounded-lg transition-colors ${
                      isDark
                        ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
                <PaymentMethodForm
                  onSuccess={handlePaymentMethodAdded}
                  onCancel={() => setShowAddPayment(false)}
                />
              </div>
            ) : (
              <PaymentMethodList onAddNew={() => setShowAddPayment(true)} />
            )}
          </div>
        )}

        {activeTab === 'invoices' && (
          <div>
            <h2 className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('billing.invoiceHistory')}
            </h2>
            <div className={`rounded-xl shadow-lg overflow-hidden ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white'}`}>
              <InvoiceHistory />
            </div>
          </div>
        )}
      </div>
    </StripeProvider>
  );
};

export default BillingDashboard;
